const DEBOUNCE_MS = 180;
const MIN_QUERY = 2;

const Search = {
  desktop: null,
  mobile: null,
  products: [],
  header: null,

  createInstance: function (root, mode) {
    if (!root) return null;

    const input = root.querySelector('[data-rd-search-input]');
    const form = root.querySelector('[data-rd-search-form]');
    const list = root.querySelector('[data-rd-search-list]');
    const heading = root.querySelector('[data-rd-search-heading]');
    const openButtons = document.querySelectorAll(`[data-rd-search-open="${mode}"]`);
    // Fallback: openers sem modo (legado) abrem o mobile.
    const legacyOpeners = mode === 'mobile'
      ? document.querySelectorAll('[data-rd-search-open]:not([data-rd-search-open="desktop"]):not([data-rd-search-open="mobile"])')
      : [];

    return {
      root,
      mode,
      input,
      form,
      list,
      heading,
      openButtons: [...openButtons, ...legacyOpeners],
      defaultSuggestionsHtml: list ? list.innerHTML : '',
      debounceTimer: null,
    };
  },

  isOpen: function (instance) {
    return Boolean(instance && instance.root.classList.contains('is-open'));
  },

  open: function (instance) {
    if (!instance) return;

    // Só um modo aberto por vez.
    if (instance.mode === 'desktop' && Search.isOpen(Search.mobile)) Search.close(Search.mobile);
    if (instance.mode === 'mobile' && Search.isOpen(Search.desktop)) Search.close(Search.desktop);

    // Fecha mega menu se estiver aberto (desktop).
    if (instance.mode === 'desktop' && Search.header) {
      Search.header.classList.remove('has-submenu-open');
      Search.header.querySelectorAll('[data-rd-menu-item].is-open').forEach(item => {
        item.classList.remove('is-open');
        const toggle = item.querySelector('[data-rd-submenu-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
      document.querySelectorAll('[data-rd-submenu].is-open').forEach(panel => {
        panel.classList.remove('is-open');
        panel.hidden = true;
      });
      const overlay = document.getElementById('overlay');
      if (overlay) overlay.classList.remove('-visible');
    }

    if (instance.mode === 'desktop') {
      instance.root.hidden = false;
      if (Search.header) Search.header.classList.add('is-search-open');
    }

    if (instance.mode === 'mobile') {
      // Inline no drawer: esconde "Buscar", mostra o form.
      instance.openButtons.forEach(button => {
        button.hidden = true;
      });
      if (instance.form) instance.form.hidden = false;
    }

    instance.root.classList.add('is-open');

    instance.openButtons.forEach(button => {
      button.setAttribute('aria-expanded', 'true');
    });

    requestAnimationFrame(() => {
      if (instance.input) instance.input.focus();
    });
  },

  close: function (instance) {
    if (!instance || !Search.isOpen(instance)) return;

    instance.root.classList.remove('is-open');

    if (instance.mode === 'desktop' && Search.header) {
      Search.header.classList.remove('is-search-open');
    }

    if (instance.mode === 'mobile') {
      if (instance.form) instance.form.hidden = true;
      instance.openButtons.forEach(button => {
        button.hidden = false;
      });
    }

    instance.openButtons.forEach(button => {
      button.setAttribute('aria-expanded', 'false');
    });

    if (instance.input) {
      instance.input.value = '';
      Search.renderDefault(instance);
    }

    if (instance.mode === 'desktop') {
      const onEnd = (event) => {
        if (event.target !== instance.root) return;
        if (event.propertyName !== 'opacity' && event.propertyName !== 'transform') return;
        instance.root.hidden = true;
        instance.root.removeEventListener('transitionend', onEnd);
      };

      instance.root.addEventListener('transitionend', onEnd);
      setTimeout(() => {
        if (!Search.isOpen(instance)) instance.root.hidden = true;
      }, 360);
    }
  },

  toggle: function (instance) {
    if (!instance) return;
    if (Search.isOpen(instance)) Search.close(instance);
    else Search.open(instance);
  },

  renderDefault: function (instance) {
    if (!instance || !instance.list || !instance.heading) return;
    instance.heading.textContent = 'Sugestões';
    instance.list.innerHTML = instance.defaultSuggestionsHtml;
  },

  renderMatches: function (instance, query) {
    if (!instance || !instance.list || !instance.heading) return;

    const normalized = query.trim().toLowerCase();
    const matches = Search.products
      .filter(product => product.name && product.name.toLowerCase().includes(normalized))
      .slice(0, 8);

    instance.heading.textContent = matches.length ? 'Produtos' : 'Sugestões';

    if (matches.length === 0) {
      instance.list.innerHTML = instance.defaultSuggestionsHtml;
      return;
    }

    instance.list.innerHTML = matches.map(product => {
      const label = Search.highlight(product.name, normalized);
      const className = instance.mode === 'desktop'
        ? 'rd-search-inline__suggestion'
        : 'rd-search__suggestion';
      return `<li><a class="${className}" href="${product.url}">${label}</a></li>`;
    }).join('');
  },

  highlight: function (text, query) {
    const index = text.toLowerCase().indexOf(query);
    if (index < 0) return Search.escape(text);

    const before = Search.escape(text.slice(0, index));
    const match = Search.escape(text.slice(index, index + query.length));
    const after = Search.escape(text.slice(index + query.length));
    return `${before}<mark>${match}</mark>${after}`;
  },

  escape: function (value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  },

  onInput: function (instance) {
    if (!instance.list) return;
    clearTimeout(instance.debounceTimer);

    instance.debounceTimer = setTimeout(() => {
      const query = instance.input?.value || '';
      if (query.trim().length < MIN_QUERY) {
        Search.renderDefault(instance);
        return;
      }
      Search.renderMatches(instance, query);
    }, DEBOUNCE_MS);
  },

  bindInstance: function (instance) {
    if (!instance) return;

    instance.openButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        if (instance.mode === 'desktop') Search.toggle(instance);
        else Search.open(instance);
      });
    });

    instance.root.querySelectorAll('[data-rd-search-close]').forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        Search.close(instance);
      });
    });

    if (instance.input) {
      instance.input.addEventListener('input', () => Search.onInput(instance));
    }
  },

  bindGlobal: function () {
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (Search.isOpen(Search.desktop)) Search.close(Search.desktop);
      // Mobile: Escape cancela o campo; o drawer trata o próximo Escape.
      if (Search.isOpen(Search.mobile)) {
        event.stopImmediatePropagation();
        Search.close(Search.mobile);
      }
    });

    document.addEventListener('pointerdown', (event) => {
      const desktop = Search.desktop;
      if (!Search.isOpen(desktop)) return;

      const inField = desktop.root.contains(event.target);
      const inOpener = desktop.openButtons.some(btn => btn.contains(event.target));
      if (inField || inOpener) return;

      Search.close(desktop);
    });
  },

  init: function () {
    Search.header = document.querySelector('[data-rd-header]');

    const desktopRoot = document.querySelector('[data-rd-search-inline]');
    const mobileRoot = document.querySelector('[data-rd-search="mobile"]');

    Search.desktop = Search.createInstance(desktopRoot, 'desktop');
    Search.mobile = Search.createInstance(mobileRoot, 'mobile');

    const productsNode = document.querySelector('[data-rd-search-products]');
    if (productsNode) {
      try {
        Search.products = JSON.parse(productsNode.textContent || '[]');
      } catch (error) {
        Search.products = [];
      }
    }

    Search.bindInstance(Search.desktop);
    Search.bindInstance(Search.mobile);
    Search.bindGlobal();

    window.rdSearch = {
      open: (mode = 'desktop') => Search.open(mode === 'mobile' ? Search.mobile : Search.desktop),
      close: () => {
        Search.close(Search.desktop);
        Search.close(Search.mobile);
      },
      isMobileOpen: () => Search.isOpen(Search.mobile),
      closeMobile: () => Search.close(Search.mobile),
    };
  },
};

export default Search;
